import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  arrayRemove,
} from "firebase/firestore";
import db from "./FirestoreConfig";
import { Client } from "./Client";
import { runInAction } from "mobx";

class CoachFirestoreListener {
  clientsListener = null;
  invitationsListener = null;

  chatsListener = null;

  constructor(coach) {
    this.coach = coach;
  }

  /* CLIENTS LISTENER */
  async setupClientsListener(coach) {
    return new Promise((resolve, reject) => {
      if (this.clientsListener) {
        console.log("Removing old clients listener...");
        this.removeClientsListener();
      }

      if (!coach?.id) {
        console.warn("No coach ID provided, skipping clients listener setup.");
        return reject(new Error("No coach ID provided"));
      }

      console.log("Setting up clients listener for coach:", coach.id);

      let initialRun = true;

      const clientsQuery = query(
        collection(db, "users"),
        where("coach_id", "==", coach.id)
      );

      this.clientsListener = onSnapshot(
        clientsQuery,
        (querySnapshot) => {
          try {
            runInAction(() => {
              const existingClientIds = new Set(
                coach.clients.map((client) =>
                  client.id.toString().toLowerCase()
                )
              );
              querySnapshot.docChanges().forEach((change) => {
                const clientData = change.doc.data();
                const clientId = change.doc.id.toLowerCase();

                switch (change.type) {
                  case "added":
                    if (!existingClientIds.has(clientId)) {
                      // console.log("User added", clientId);
                      coach.fetchClient(clientData);
                      existingClientIds.add(clientId);
                    }
                    break;

                  case "modified":
                    const foundClient = coach.clients.find(
                      (client) => client.id.toLowerCase() === clientId
                    );
                    if (foundClient) {
                      foundClient.updateClient(clientData);
                    }

                    break;
                  case "removed":
                    console.log("user removed");
                    coach.clients = coach.clients.filter(
                      (client) =>
                        client.id.toString().toLowerCase() !== clientId
                    );
                    existingClientIds.delete(clientId);
                    break;
                }
              });
            });

            if (initialRun) {
              initialRun = false;
              resolve();
            }
          } catch (err) {
            console.error("Error processing clients listener:", err);
            if (initialRun) {
              initialRun = false;
              reject(err);
            }
          }
        },
        (error) => {
          console.error("Error with clients listener:", error);
          // crashlytics().recordError(error);
          if (error.code === "permission-denied") {
            console.log("Permission error, stopping clients listener.");
            this.removeClientsListener();
          }
          reject(error);
        }
      );
    });
  }

  removeClientsListener() {
    if (this.clientsListener) {
      console.log("Removing clients listener...");
      this.clientsListener();
      this.clientsListener = null;
    } else {
      console.log("No active clients listener to remove.");
    }
  }

  /* INVITATIONS LISTENER */
  setupInvitationsListener(coach) {
    return new Promise((resolve, reject) => {
      if (this.invitationsListener) {
        console.log("Removing old invitations listener...");
        this.removeInvitationsListener();
      }

      console.log("Setting up invitations listener for email:", coach.email);

      let initialRun = true;
      const invitationsQuery = query(
        collection(db, "invitations"),
        where("coach_id", "==", coach.id)
      );

      this.invitationsListener = onSnapshot(
        invitationsQuery,
        (querySnapshot) => {
          try {
            runInAction(() => {
              const existingInvitationIds = new Set(
                coach.clients.map((client) => client.invitationId)
              );

              querySnapshot.docChanges().forEach((change) => {
                const dataInvitation = change.doc.data();
                const invitationId = change.doc.id;

                switch (change.type) {
                  case "added":
                    if (!existingInvitationIds.has(invitationId)) {
                      console.log("New invitation", invitationId);

                      // Determine status based on emailStatus
                      const invitationStatus =
                        dataInvitation.emailStatus === "error"
                          ? "error-string"
                          : "pending-string";

                      const newClient = new Client(
                        undefined,
                        dataInvitation.new_client_email,
                        dataInvitation.new_client_email,
                        "",
                        dataInvitation.new_client_email,
                        "",
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        coach,
                        undefined,
                        false,
                        undefined,
                        undefined,
                        invitationStatus,
                        invitationId
                      );
                      coach.clients.push(newClient);
                      existingInvitationIds.add(invitationId);
                    }
                    break;

                  case "modified":
                    // Detect if emailStatus changed to "error"
                    if (dataInvitation.emailStatus === "error") {
                      console.log(
                        "Invitation email failed for:",
                        dataInvitation.new_client_email
                      );

                      // Update the client status to "error-string"
                      const clientToUpdate = coach.clients.find(
                        (client) => client.invitationId === invitationId
                      );
                      if (
                        clientToUpdate &&
                        clientToUpdate.status !== "error-string"
                      ) {
                        clientToUpdate.setProperty("status", "error-string");

                        if (
                          coach.showToast &&
                          typeof coach.showToast === "function"
                        ) {
                          coach.showToast("invitationEmailError-string", {
                            type: "error",
                            description:
                              "invitationEmailErrorDescription-string",
                            variables: {
                              clientEmail: dataInvitation.new_client_email,
                            },
                          });
                        }
                      }
                    }
                    break;

                  case "removed":
                    console.log("Removed invitation", invitationId);
                    const clientIndex = coach.clients.findIndex(
                      (client) => client.invitationId === invitationId
                    );

                    if (clientIndex !== -1) {
                      coach.clients.splice(clientIndex, 1);
                      existingInvitationIds.delete(invitationId);
                      console.log(
                        `Client with ID ${invitationId} removed from coach.clients.`
                      );
                    }
                    break;
                }
              });
            });

            if (initialRun) {
              initialRun = false;
              resolve();
            }
          } catch (err) {
            console.error("Error processing invitations listener:", err);
            if (initialRun) {
              initialRun = false;
              reject(err);
            }
          }
        },
        (error) => {
          console.error("Error with invitations listener:", error);
          if (error.code === "permission-denied") {
            console.log("Permission error, stopping invitations listener.");
            this.removeInvitationsListener();
          }
          reject(error);
        }
      );
    });
  }

  removeInvitationsListener() {
    if (this.invitationsListener) {
      console.log("Removing invitations listener...");
      this.invitationsListener();
      this.invitationsListener = null;
    } else {
      console.log("No active invitations listener to remove.");
    }
  }

  /* CHATS LISTENER */
  async setupChatsListener(coach) {
    return new Promise((resolve, reject) => {
      if (this.chatsListener) {
        console.log("Removing old chats listener...");
        this.removeChatsListener();
      }

      if (!coach?.id) {
        console.warn("No coach ID provided, skipping chats listener setup.");
        return reject(new Error("No coach ID provided"));
      }

      console.log("Setting up chats listener for coach:", coach.id);

      let initialRun = true;

      const chatsQuery = query(collection(db, "coaches", coach.id, "chats"));

      this.chatsListener = onSnapshot(
        chatsQuery,
        { includeMetadataChanges: false },
        (querySnapshot) => {
          try {
            querySnapshot.docChanges().forEach((change) => {
              const chatData = change.doc.data();

              const client = coach.clients.find(
                (client) =>
                  client.id.toString().toLowerCase() ===
                  change.doc?.id.toLowerCase()
              );

              if (
                (change.type === "added" || change.type === "modified") &&
                client
              ) {
                // console.log("new messages for client", change.doc.id);

                if (client.firestoreListener.chatListener === null) {
                  client.fetchChatLastMessages(chatData);
                }

                if (
                  client.firestoreListener.chatListener != null &&
                  coach.showingChatList
                ) {
                  const updateData = {};
                  if (
                    typeof chatData.unread_count_for_coach === "number" &&
                    chatData.unread_count_for_coach > 0
                  ) {
                    updateData.unread_count_for_coach = 0;
                  }

                  if (
                    Array.isArray(chatData.last_messages_from_client) &&
                    chatData.last_messages_from_client.length > 5
                  ) {
                    console.log(
                      "chatData.last_messages_from_client",
                      chatData.last_messages_from_client
                    );
                    const sortedMessages = [
                      ...chatData.last_messages_from_client,
                    ].sort((a, b) => a.timestamp - b.timestamp);
                    const messagesToRemove = sortedMessages.slice(
                      0,
                      sortedMessages.length - 5
                    );
                    updateData.last_messages_from_client = arrayRemove(
                      ...messagesToRemove
                    );
                    console.log(
                      "Removed messages from last_messages_from_client:",
                      messagesToRemove
                    );
                  }

                  if (Object.keys(updateData).length > 0) {
                    updateDoc(change.doc.ref, updateData)
                      .then(() => {
                        console.log("Firebase update successful");
                      })
                      .catch((err) => {
                        console.error("Firebase update failed:", err);
                      });
                  }
                } else {
                  client.setProperty(
                    "unreadCount",
                    chatData.unread_count_for_coach
                  );
                }
                if (typeof chatData.unread_mark === "boolean") {
                  client.setProperty("unreadMark", chatData.unread_mark);
                } else {
                  client.setProperty("unreadMark", false);
                }
              } else if (change.type === "removed") {
                console.log("chat removed");
                if (client) {
                  client.messages = [];
                }
              }
            });

            if (initialRun) {
              initialRun = false;
              resolve();
            }
          } catch (err) {
            console.error("Error processing chats listener:", err);
            if (initialRun) {
              initialRun = false;
              reject(err);
            }
          }
        },
        (error) => {
          console.error("Error with chats listener:", error);
          // crashlytics().recordError(error);
          if (error.code === "permission-denied") {
            console.log("Permission error, stopping chats listener.");
            this.removeChatsListener();
          }
          reject(error);
        }
      );
    });
  }

  removeChatsListener() {
    if (this.chatsListener) {
      console.log("Removing chats listener...");
      this.chatsListener();
      this.chatsListener = null;
    } else {
      console.log("No active chats listener to remove.");
    }
  }
}

export default CoachFirestoreListener;
